/**
 * Operational axle/wheel tyre schematic (not CAD).
 * Cards show number, condition, tread, pressure target/exception, attention.
 */

import React from 'react';
import { Empty, Space, Tag, Typography } from 'antd';
import PmvApplyLayoutEmpty from '@/components/pmv/PmvApplyLayoutEmpty';
import type { PmvTyreSchematicCard, PmvTyreSchematicResponse } from '@/services/pmv.service';

const { Text } = Typography;

function conditionColor(code: string | null): string {
  if (!code) return 'default';
  const u = code.toUpperCase();
  if (u === 'NEW' || u === 'GOOD') return 'success';
  if (u === 'SERVICEABLE') return 'processing';
  if (u === 'WORN' || u === 'REPAIRABLE' || u === 'RETREADABLE') return 'warning';
  if (u === 'DAMAGED' || u === 'UNSERVICEABLE' || u === 'SCRAP') return 'error';
  return 'default';
}

const TyrePositionCard: React.FC<{ card: PmvTyreSchematicCard }> = ({ card }) => {
  const vacant = card.status === 'VACANT';
  return (
    <div
      role="group"
      aria-label={`${card.display_label} ${vacant ? 'vacant' : card.component_number || 'occupied'}`}
      style={{
        minWidth: 132,
        maxWidth: 160,
        padding: '10px 12px',
        border: '1px solid var(--ant-color-border, #d9d9d9)',
        borderRadius: 6,
        background: vacant ? 'var(--ant-color-fill-quaternary, #fafafa)' : '#fff',
        textAlign: 'center',
      }}
    >
      <Text strong style={{ fontSize: 13 }}>
        {card.position_code}
      </Text>
      <div style={{ marginTop: 4 }}>
        {vacant ? (
          <Tag>Vacant</Tag>
        ) : (
          <Text style={{ fontSize: 12 }}>{card.component_number}</Text>
        )}
      </div>
      {!vacant && (
        <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 6 }}>
          {card.condition_code && (
            <Tag color={conditionColor(card.condition_code)} style={{ margin: 0 }}>
              {card.condition_code}
            </Tag>
          )}
          <Text type="secondary" style={{ fontSize: 11 }}>
            Tread:{' '}
            {card.tread_depth_mm != null ? `${card.tread_depth_mm} mm` : '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Press tgt:{' '}
            {card.pressure_target_psi != null ? `${card.pressure_target_psi} psi` : '—'}
          </Text>
          {card.pressure_exception && (
            <Tag color="error" style={{ margin: 0 }}>
              {card.pressure_exception}
            </Tag>
          )}
          {card.attention?.length > 0 &&
            card.attention.map((a) => (
              <Tag key={a} color="orange" style={{ margin: 0 }}>
                {a}
              </Tag>
            ))}
          {card.size_code && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {card.size_code}
            </Text>
          )}
        </Space>
      )}
    </div>
  );
};

function groupCards(
  data: PmvTyreSchematicResponse
): Array<{ key: string; label: string; cards: PmvTyreSchematicCard[] }> {
  const byCode = new Map(data.cards.map((c) => [c.position_code, c]));
  if (data.schematic_groups?.length) {
    return data.schematic_groups.map((g) => ({
      key: g.group_key,
      label: g.label || g.group_key,
      cards: g.positions
        .map((p) => byCode.get(p.position_code))
        .filter((c): c is PmvTyreSchematicCard => Boolean(c)),
    }));
  }
  const spare = data.cards.filter((c) => c.is_spare);
  const axle = data.cards.filter((c) => !c.is_spare);
  const groups: Array<{ key: string; label: string; cards: PmvTyreSchematicCard[] }> = [];
  const axles = new Map<string, PmvTyreSchematicCard[]>();
  for (const c of axle) {
    const k = c.axle_group || 'AXLE';
    if (!axles.has(k)) axles.set(k, []);
    axles.get(k)!.push(c);
  }
  for (const [k, cards] of axles) {
    groups.push({ key: k, label: k.replace(/_/g, ' '), cards });
  }
  if (spare.length) groups.push({ key: 'SPARE', label: 'Spare', cards: spare });
  return groups;
}

export const TyreSchematicView: React.FC<{
  data: PmvTyreSchematicResponse | null | undefined;
  loading?: boolean;
  pmvProfileId?: string;
  onLayoutApplied?: () => void;
  onSelectCard?: (card: PmvTyreSchematicCard) => void;
  /** When true, vacant cards are clickable (install flow). */
  allowVacantSelect?: boolean;
}> = ({ data, pmvProfileId, onLayoutApplied, onSelectCard, allowVacantSelect }) => {
  if (!data) return null;
  if (data.empty_reason) {
    if (pmvProfileId) {
      return (
        <PmvApplyLayoutEmpty
          profileId={pmvProfileId}
          description={data.empty_reason}
          onApplied={onLayoutApplied}
        />
      );
    }
    return <Empty description={data.empty_reason} />;
  }
  if (!data.cards?.length) {
    return <Empty description="No tyre positions on this equipment layout" />;
  }

  const groups = groupCards(data);

  return (
    <div
      role="region"
      aria-label="Tyre position schematic"
      style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}
    >
      <Text type="secondary" style={{ textAlign: 'center', maxWidth: 480 }}>
        Front ↑ — operational wheel map (not a CAD drawing). Click an occupied tyre to inspect
        {allowVacantSelect ? ', or a vacant slot to install' : ''}.
      </Text>
      {groups.map((g) => (
        <div key={g.key} style={{ width: '100%' }}>
          <Text
            type="secondary"
            style={{ display: 'block', textAlign: 'center', marginBottom: 8, letterSpacing: 0.5 }}
          >
            {g.label}
          </Text>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            {g.cards.map((card) => {
              const vacant = card.status === 'VACANT';
              const canClick = vacant
                ? Boolean(allowVacantSelect)
                : Boolean(card.component_id);
              return (
                <button
                  key={card.position_id}
                  type="button"
                  onClick={() => canClick && onSelectCard?.(card)}
                  disabled={!canClick}
                  style={{
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: canClick ? 'pointer' : 'default',
                  }}
                >
                  <TyrePositionCard card={card} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TyreSchematicView;
